<template>
  <div class="rich-text-editor-wrapper" :class="{ 'rte-dark': isDark }" style="position: relative;">
    <div :id="editorId" ref="editorContainer"></div>
    <div v-if="uploading" class="rte-upload-overlay">
      <i class="fa fa-spinner fa-spin"></i> Uploading image...
    </div>
  </div>
</template>

<script>
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { sanitizeQuillHtml } from '@/util/quillSanitize';
import axios from 'axios';
import { Auth } from 'aws-amplify';
const { API_LOCATION } = require('@/api-config');

// Get Parchment for creating style-based attributors
const Parchment = Quill.import('parchment');

// Register inline style attributors for TinyMCE compatibility
// These allow Quill to preserve and create inline styles instead of classes

// Color as inline style
const ColorStyle = new Parchment.StyleAttributor('color', 'color', {
  scope: Parchment.Scope.INLINE
});
Quill.register(ColorStyle, true);

// Background color as inline style
const BackgroundStyle = new Parchment.StyleAttributor('background', 'background-color', {
  scope: Parchment.Scope.INLINE
});
Quill.register(BackgroundStyle, true);

// Font family as inline style (for TinyMCE compatibility)
const FontStyle = new Parchment.StyleAttributor('font', 'font-family', {
  scope: Parchment.Scope.INLINE
});
Quill.register(FontStyle, true);

// Font size as inline style
const SizeStyle = new Parchment.StyleAttributor('size', 'font-size', {
  scope: Parchment.Scope.INLINE
});
Quill.register(SizeStyle, true);

// Text alignment as inline style on block elements
const AlignStyle = new Parchment.StyleAttributor('align', 'text-align', {
  scope: Parchment.Scope.BLOCK
});
Quill.register(AlignStyle, true);

export default {
  name: 'RichTextEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    height: {
      type: [Number, String],
      default: 300
    },
    placeholder: {
      type: String,
      default: 'Enter text...'
    },
    toolbar: {
      type: String,
      default: 'full',
      validator: (value) => ['full', 'minimal', 'basic', 'comment'].includes(value)
    },
    dark: {
      type: Boolean,
      default: false
    },
    allowImages: {
      type: Boolean,
      default: true
    },
    // Opt-in: load HTML with Quill's default clipboard matcher (matchVisual: true)
    // so block boundaries (e.g. a heading above a list) survive the load instead of
    // merging into the following item. Defaults to false to preserve legacy behavior
    // for every existing consumer; only opt in editors that hold externally-authored HTML.
    faithfulLoad: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      quill: null,
      editorId: `editor-${Math.random().toString(36).substr(2, 9)}`,
      isUpdating: false,
      uploading: false,
      // Tracks the body's white-content class so a stale `dark` prop from a
      // parent (theme applied or toggled after the parent computed it) never
      // paints dark-mode toolbar strokes on a light page.
      pageIsLight: typeof document !== 'undefined' && document.body.classList.contains('white-content'),
      themeObserver: null
    };
  },
  computed: {
    // Dark styling only when the consumer asked for it AND the page is dark.
    isDark() {
      return this.dark && !this.pageIsLight;
    },
    defaultTextColor() {
      return this.isDark ? '#ffffff' : '#000000';
    },
    fontList() {
      // Using actual font-family values for inline style compatibility
      // Matching TinyMCE font list
      return [
        'Andale Mono, monospace',
        'Arial, Helvetica, sans-serif',
        'Arial Black, Gadget, sans-serif',
        'Book Antiqua, Palatino, serif',
        'Comic Sans MS, cursive, sans-serif',
        'Courier New, Courier, monospace',
        'Georgia, serif',
        'Helvetica, Arial, sans-serif',
        'Impact, Charcoal, sans-serif',
        'Oswald, sans-serif',
        'Symbol',
        'Tahoma, Geneva, sans-serif',
        'Terminal, Monaco, monospace',
        'Times New Roman, Times, serif',
        'Trebuchet MS, Helvetica, sans-serif',
        'Ubuntu, sans-serif',
        'Verdana, Geneva, sans-serif',
        'Webdings',
        'Wingdings, Zapf Dingbats'
      ];
    },
    toolbarOptions() {
      const toolbars = {
        full: [
          [{ 'font': this.fontList }],
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }, 'code-block'],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'color': this.colorPalette }, { 'background': [] }],
          [{ 'align': [] }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          ['link'],
          ['clean']
        ],
        basic: [
          [{ 'font': this.fontList }],
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline'],
          [{ 'color': this.colorPalette }, { 'background': [] }],
          [{ 'align': [] }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          ['link', 'image'],
          ['clean']
        ],
        minimal: [
          ['bold', 'italic', 'underline'],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          ['link', 'image'],
          ['clean']
        ],
        comment: [
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          ['link', 'image'],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          [{ 'color': this.colorPalette }, { 'background': [] }],
          ['clean']
        ]
      };
      const selected = toolbars[this.toolbar] || toolbars.full;
      // Strip the image button when images are disabled for this field (e.g. short text highlights)
      if (!this.allowImages) {
        return selected
          .map(group => Array.isArray(group) ? group.filter(item => item !== 'image') : group)
          .filter(group => !Array.isArray(group) || group.length > 0);
      }
      return selected;
    },
    colorPalette() {
      return [
        '#000000', '#e60000', '#ff9900', '#ffff00', '#008a00', '#0066cc', '#9933ff',
        '#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff',
        '#bbbbbb', '#f06666', '#ffc266', '#ffff66', '#66b966', '#66a3e0', '#c285ff',
        '#888888', '#a10000', '#b26b00', '#b2b200', '#006100', '#0047b2', '#6b24b2',
        '#444444', '#5c0000', '#663d00', '#666600', '#003700', '#002966', '#3d1466'
      ];
    },
    editorHeight() {
      const h = typeof this.height === 'number' ? this.height : parseInt(this.height, 10);
      return isNaN(h) ? 300 : h;
    }
  },
  watch: {
    value(newVal) {
      if (this.quill && !this.isUpdating) {
        const currentContent = this.quill.root.innerHTML;
        if (newVal !== currentContent && newVal !== this.enforceDefaultTextColor(currentContent)) {
          // Clear and re-paste to preserve HTML formatting
          if (newVal) {
            let html = this.isDark ? this.enforceDefaultTextColor(newVal) : newVal;
            this.setHtmlSilently(html);
          } else {
            this.quill.setText('');
          }
        }
      }
    }
  },
  methods: {
    initQuill() {
      this.quill = new Quill(this.$refs.editorContainer, {
        theme: 'snow',
        placeholder: this.placeholder,
        modules: {
          toolbar: this.toolbarOptions,
          clipboard: {
            // false (legacy) can merge adjacent blocks on load; faithfulLoad opts
            // into Quill's default (true) to keep block boundaries for foreign HTML.
            matchVisual: this.faithfulLoad
          }
        }
      });

      // Set initial content without focusing: the clipboard paste API sets a selection,
      // which focuses the editor and scrolls it into view (drawers opened mid-body).
      if (this.value) {
        this.setHtmlSilently(this.value);
      }

      // Default text color on init (skip in dark mode, CSS handles it). Only the root
      // style is set here: quill.format() takes a selection, which focuses the editor
      // and scrolls it into view when it mounts inside a drawer. The typed-text color
      // is enforced on selection-change and toolbar clicks below instead.
      if (!this.isDark) {
        this.quill.root.style.color = '#000000';
      }

      // Set editor height (skip if 'auto' to let content determine size)
      if (this.height !== 'auto') {
        this.quill.root.style.minHeight = `${this.editorHeight}px`;
      }

      // Handle toolbar clicks - force black color (skip in dark mode)
      if (!this.isDark) {
        const toolbarEl = this.$refs.editorContainer.querySelector('.ql-toolbar');
        if (toolbarEl) {
          toolbarEl.addEventListener('click', () => {
            setTimeout(() => {
              this.forceDefaultTextColor();
            }, 10);
          });
        }
      }

      // Override default image handler with S3 upload (only when images are allowed)
      if (this.allowImages) {
        const toolbarModule = this.quill.getModule('toolbar');
        toolbarModule.addHandler('image', () => {
          this.handleImageUpload();
        });
      }

      // Handle text changes
      this.quill.on('text-change', (delta, oldDelta, source) => {
        if (source !== 'user') return;

        this.isUpdating = true;

        if (!this.isDark) {
          const selection = this.quill.getSelection();
          if (selection) {
            const justInsertedNewline = delta.ops.some(op => op.insert === '\n');
            const hasListFormatting = delta.ops.some(op =>
              op.attributes && (op.attributes.list || op.attributes.indent !== undefined)
            );

            if (justInsertedNewline || hasListFormatting) {
              setTimeout(() => {
                const formats = this.quill.getFormat(selection.index, 1);
                if (!formats.color) {
                  this.quill.format('color', '#000000');
                }
                this.forceDefaultTextColor();
              }, 0);
            }
          }
        }

        // Emit the sanitized content
        const content = this.isDark
          ? sanitizeQuillHtml(this.quill.root.innerHTML)
          : this.enforceDefaultTextColor(this.quill.root.innerHTML);
        this.$emit('input', content);

        this.$nextTick(() => {
          this.isUpdating = false;
        });
      });

      // Handle selection changes for toolbar state (skip in dark mode)
      if (!this.isDark) {
        this.quill.on('selection-change', (range) => {
          if (range) {
            this.forceDefaultTextColor();
          }
        });
      }
    },

    handleImageUpload() {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();

      input.onchange = async () => {
        const file = input.files[0];
        if (!file) return;

        this.uploading = true;
        try {
          const session = await Auth.currentSession();
          const token = session.getIdToken().getJwtToken();
          const config = { headers: { Authorization: token } };

          const payload = { filePath: file.name, contentType: file.type };
          const res = await axios.post(API_LOCATION + 'upload', payload, config);
          const presignedUrl = res.data;

          await axios.put(presignedUrl, file, {
            headers: { 'Content-Type': file.type }
          });

          const cleanUrl = presignedUrl.split('?')[0];
          const range = this.quill.getSelection(true);
          this.quill.insertEmbed(range.index, 'image', cleanUrl);
          this.quill.setSelection(range.index + 1);
        } catch (err) {
          console.error('Image upload failed:', err);
        } finally {
          this.uploading = false;
        }
      };
    },

    isDefaultBlack(color) {
      return !color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)'
        || color === '#000000' || color === 'rgb(0, 0, 0)' || color === 'black';
    },

    forceDefaultTextColor() {
      if (!this.quill) return;

      const editorEl = this.quill.root;
      if (editorEl) {
        editorEl.querySelectorAll('li, li span, ol, ul, h1, h2, h3, h4, h5, h6, p, pre, code').forEach(el => {
          const color = el.style.color;
          if (this.isDark ? this.isDefaultBlack(color) : !color) {
            el.style.color = this.defaultTextColor;
          }
        });
      }
    },

    enforceDefaultTextColor(html) {
      if (!html) return html;

      html = sanitizeQuillHtml(html);

      const container = document.createElement('div');
      container.innerHTML = html;

      container.querySelectorAll('p, span, div, li, ol, ul, h1, h2, h3, h4, h5, h6, pre, code').forEach(element => {
        const currentColor = element.style.color;

        if (this.isDark ? this.isDefaultBlack(currentColor) : this.isDefaultBlack(currentColor)) {
          element.style.color = this.defaultTextColor;
        }
      });

      return container.innerHTML;
    },

    // Public method to get content
    getContent() {
      if (!this.quill) return '';
      if (this.isDark) {
        return sanitizeQuillHtml(this.quill.root.innerHTML);
      }
      return this.enforceDefaultTextColor(this.quill.root.innerHTML);
    },

    // Replace the editor content from HTML without touching selection or focus.
    setHtmlSilently(html) {
      const delta = this.quill.clipboard.convert({ html });
      this.quill.setContents(delta, 'silent');
    },

    // Public method to set content
    setContent(html) {
      if (this.quill) {
        if (html) {
          this.setHtmlSilently(html);
        } else {
          this.quill.setText('');
        }
      }
    },

    // Public method to clear content
    clear() {
      if (this.quill) {
        this.quill.setText('');
      }
    },

    // Public method to focus the editor
    focus() {
      if (this.quill) {
        this.quill.focus();
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initQuill();
    });
    if (typeof MutationObserver !== 'undefined') {
      this.themeObserver = new MutationObserver(() => {
        this.pageIsLight = document.body.classList.contains('white-content');
      });
      this.themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
  },
  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = null;
    }
    if (this.quill) {
      this.quill = null;
    }
  }
};
</script>

<style>
.rich-text-editor-wrapper {
  width: 100%;
}

.rich-text-editor-wrapper .ql-container {
  font-size: 14px;
  font-family: Arial, sans-serif;
}

.rich-text-editor-wrapper .ql-editor {
  color: #000000;
  background-color: #ffffff;
}

.rich-text-editor-wrapper .ql-editor.ql-blank::before {
  color: #999999;
  font-style: italic;
}

/* Set default text color to black (without !important so inline styles can override) */
.rich-text-editor-wrapper .ql-editor ul,
.rich-text-editor-wrapper .ql-editor ol,
.rich-text-editor-wrapper .ql-editor li,
.rich-text-editor-wrapper .ql-editor h1,
.rich-text-editor-wrapper .ql-editor h2,
.rich-text-editor-wrapper .ql-editor h3,
.rich-text-editor-wrapper .ql-editor h4,
.rich-text-editor-wrapper .ql-editor h5,
.rich-text-editor-wrapper .ql-editor h6,
.rich-text-editor-wrapper .ql-editor p,
.rich-text-editor-wrapper .ql-editor pre,
.rich-text-editor-wrapper .ql-editor code {
  color: #000000;
}

/* Code block styling */
.rich-text-editor-wrapper .ql-editor pre.ql-syntax {
  background-color: #f4f4f4;
  border-radius: 4px;
  padding: 10px;
  font-family: 'Courier New', Courier, monospace;
}

/* Toolbar styling */
.rich-text-editor-wrapper .ql-toolbar {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background-color: #f8f9fa;
}

.rich-text-editor-wrapper .ql-container {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

/* Upload overlay */
.rte-upload-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 8px 12px;
  font-size: 13px;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  z-index: 10;
}

/* Fix for color picker dropdown z-index */
.rich-text-editor-wrapper .ql-picker-options {
  z-index: 1000;
}

/* Font picker width to accommodate longer font names */
.ql-snow .ql-picker.ql-font {
  width: 150px;
}

/* Style each font option with its own font for preview and clean label */
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Andale Mono, monospace"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Andale Mono, monospace"]::before {
  content: 'Andale Mono';
  font-family: 'Andale Mono', monospace;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Arial, Helvetica, sans-serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Arial, Helvetica, sans-serif"]::before {
  content: 'Arial';
  font-family: Arial, Helvetica, sans-serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Arial Black, Gadget, sans-serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Arial Black, Gadget, sans-serif"]::before {
  content: 'Arial Black';
  font-family: 'Arial Black', Gadget, sans-serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Book Antiqua, Palatino, serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Book Antiqua, Palatino, serif"]::before {
  content: 'Book Antiqua';
  font-family: 'Book Antiqua', Palatino, serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Comic Sans MS, cursive, sans-serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Comic Sans MS, cursive, sans-serif"]::before {
  content: 'Comic Sans MS';
  font-family: 'Comic Sans MS', cursive, sans-serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Courier New, Courier, monospace"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Courier New, Courier, monospace"]::before {
  content: 'Courier New';
  font-family: 'Courier New', Courier, monospace;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Georgia, serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Georgia, serif"]::before {
  content: 'Georgia';
  font-family: Georgia, serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Helvetica, Arial, sans-serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Helvetica, Arial, sans-serif"]::before {
  content: 'Helvetica';
  font-family: Helvetica, Arial, sans-serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Impact, Charcoal, sans-serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Impact, Charcoal, sans-serif"]::before {
  content: 'Impact';
  font-family: Impact, Charcoal, sans-serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Oswald, sans-serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Oswald, sans-serif"]::before {
  content: 'Oswald';
  font-family: Oswald, sans-serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Symbol"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Symbol"]::before {
  content: 'Symbol';
  font-family: Arial, sans-serif; /* Symbol font - show label in readable font */
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Tahoma, Geneva, sans-serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Tahoma, Geneva, sans-serif"]::before {
  content: 'Tahoma';
  font-family: Tahoma, Geneva, sans-serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Terminal, Monaco, monospace"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Terminal, Monaco, monospace"]::before {
  content: 'Terminal';
  font-family: Terminal, Monaco, monospace;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Times New Roman, Times, serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Times New Roman, Times, serif"]::before {
  content: 'Times New Roman';
  font-family: 'Times New Roman', Times, serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Trebuchet MS, Helvetica, sans-serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Trebuchet MS, Helvetica, sans-serif"]::before {
  content: 'Trebuchet MS';
  font-family: 'Trebuchet MS', Helvetica, sans-serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Ubuntu, sans-serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Ubuntu, sans-serif"]::before {
  content: 'Ubuntu';
  font-family: Ubuntu, sans-serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Verdana, Geneva, sans-serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Verdana, Geneva, sans-serif"]::before {
  content: 'Verdana';
  font-family: Verdana, Geneva, sans-serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Webdings"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Webdings"]::before {
  content: 'Webdings';
  font-family: Arial, sans-serif; /* Symbol font - show label in readable font */
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Wingdings, Zapf Dingbats"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Wingdings, Zapf Dingbats"]::before {
  content: 'Wingdings';
  font-family: Arial, sans-serif; /* Symbol font - show label in readable font */
}

/* ─── Dark mode variant (for in-app content like tickets) ─── */
.rte-dark .ql-toolbar {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}
.rte-dark .ql-toolbar .ql-stroke {
  stroke: rgba(255, 255, 255, 0.5);
}
.rte-dark .ql-toolbar .ql-fill {
  fill: rgba(255, 255, 255, 0.5);
}
.rte-dark .ql-toolbar .ql-picker-label {
  color: rgba(255, 255, 255, 0.5);
}
.rte-dark .ql-toolbar .ql-picker-label .ql-stroke {
  stroke: rgba(255, 255, 255, 0.5);
}
.rte-dark .ql-toolbar button:hover .ql-stroke,
.rte-dark .ql-toolbar .ql-picker-label:hover .ql-stroke {
  stroke: rgba(255, 255, 255, 0.9);
}
.rte-dark .ql-toolbar button:hover .ql-fill,
.rte-dark .ql-toolbar .ql-picker-label:hover .ql-fill {
  fill: rgba(255, 255, 255, 0.9);
}
.rte-dark .ql-toolbar button.ql-active .ql-stroke {
  stroke: #e14eca;
}
.rte-dark .ql-toolbar button.ql-active .ql-fill {
  fill: #e14eca;
}
.rte-dark .ql-container {
  border-color: rgba(255, 255, 255, 0.1);
  background: transparent !important;
}
.rte-dark .ql-editor {
  background: rgba(255, 255, 255, 0.03) !important;
  color: rgba(255, 255, 255, 0.9) !important;
}
.rte-dark .ql-editor.ql-blank::before {
  color: rgba(255, 255, 255, 0.3) !important;
}
.rte-dark .ql-ui {
  color: rgba(255, 255, 255, 0.5) !important;
}
.rte-dark .ql-snow .ql-picker-options {
  background-color: #22233a;
  border-color: rgba(255, 255, 255, 0.15);
}
.rte-dark .ql-snow .ql-picker-item {
  color: rgba(255, 255, 255, 0.8);
}
.rte-dark .ql-snow .ql-picker-item:hover {
  color: #e14eca;
}
.rte-dark .ql-snow .ql-tooltip {
  background-color: #22233a;
  border-color: rgba(255, 255, 255, 0.15);
  box-shadow: none;
  color: rgba(255, 255, 255, 0.8);
}
.rte-dark .ql-snow .ql-tooltip input[type=text] {
  background-color: transparent;
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.9);
}
.rte-dark .ql-snow .ql-tooltip a {
  color: #e14eca;
}
</style>
